import { type FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { Button } from '../../../../components/Button/Button'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'
import {
  useAddSchedulingEventTypesToUserPageMutation,
  useGetSchedulingEventTypesQuery,
  useGetUserSchedulingEventTypesQuery,
  useRemoveSchedulingEventTypeFromUserPageMutation,
} from '../../../../store/scheduling/scheduling'
import { type AccountSchedulingSettings, EventTypeAccess, type SchedulingEventType, type UserSchedulingSettings } from '../../../../models/Scheduling'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { CoHostTag } from '../../../Scheduling/shared/CoHostTag'
import { isCoHostOf } from '../../../Scheduling/shared/eventTypeHostState'
import { EventTypeList } from '../../../Scheduling/shared/EventTypeList'
import { EventTypesInfoTooltip } from '../../../Scheduling/shared/EventTypesInfoTooltip'
import { BOOKING_BASE_URL } from '../../../Scheduling/shared/bookingBaseUrl'
import { SettingsCard, SettingsSection } from '../../../Setup/Scheduling/sections/SettingsSection'
import { AddSharedEventTypesModal } from './AddSharedEventTypesModal'

interface SectionProps {
  userSettings: UserSchedulingSettings
  accountSettings: AccountSchedulingSettings
}

export const YourEventTypesSection: FC<SectionProps> = ({ userSettings, accountSettings }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { trackingClient } = useTracking()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const { data: eventTypes, isLoading, isFetching } = useGetSchedulingEventTypesQuery({ accountId })
  const {
    data: userEventTypeIds,
    isLoading: isLoadingUserIds,
    isFetching: isFetchingUserIds,
  } = useGetUserSchedulingEventTypesQuery({ accountId, userId: authenticatedUser.id })
  const [addEventTypesToUserPage, { isLoading: isAdding }] = useAddSchedulingEventTypesToUserPageMutation()
  const [removeEventTypeFromUserPage, { isLoading: isRemoving }] = useRemoveSchedulingEventTypeFromUserPageMutation()
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const isSaving = isRemoving || (isFetching && !isLoading) || (isFetchingUserIds && !isLoadingUserIds)

  const accountSlug = accountSettings.publicBookingPage.accountSlug
  const userSlug = userSettings.bookingPage.slug

  const eventTypeUrl = (eventType: SchedulingEventType): string | null =>
    accountSlug && userSlug ? `https://${BOOKING_BASE_URL}/${accountSlug}/${userSlug}/${eventType.slug}` : null

  const onUserPage = new Set(userEventTypeIds ?? [])
  const accountEventTypes = (eventTypes ?? []).filter((eventType) => eventType.access === EventTypeAccess.ACCOUNT)
  const myEventTypes = accountEventTypes.filter((eventType) => onUserPage.has(eventType.id))
  const addableEventTypes = accountEventTypes.filter((eventType) => !onUserPage.has(eventType.id))

  const handleAddShared = async (selected: SchedulingEventType[]) => {
    const result = await addEventTypesToUserPage({ accountId, userId: authenticatedUser.id, eventTypeIds: selected.map((eventType) => eventType.id) })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not add event types', messages: ['Please try again.'] }))
      return
    }
    trackingClient.track({
      eventName: TrackEvent.SCHEDULING_EVENT_TYPES_ADDED_TO_BOOKING_PAGE,
      eventDetails: { eventTypeIds: selected.map((eventType) => eventType.id) },
    })
    dispatch(
      addToast({
        variant: 'success',
        title: 'Added to your page',
        messages: [
          selected.length === 1 ? `${selected[0].name} now shows on your booking page.` : `${selected.length} event types now show on your booking page.`,
        ],
      }),
    )
    setIsAddModalOpen(false)
  }

  const handleCopyLink = async (eventType: SchedulingEventType) => {
    const url = eventTypeUrl(eventType)
    if (!url) {
      Sentry.captureException(new Error('Scheduling: missing account or user slug; cannot build an event type booking link'))
      dispatch(addToast({ variant: 'error', title: 'Could not copy', messages: ['The booking link is unavailable.'] }))
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      trackingClient.track({ eventName: TrackEvent.SCHEDULING_BOOKING_LINK_COPIED, eventDetails: { source: 'profile', eventTypeId: eventType.id } })
      dispatch(addToast({ variant: 'success', title: 'Copied', messages: [`The booking link for ${eventType.name} is on the clipboard.`] }))
    } catch (error) {
      Sentry.captureException(error)
      dispatch(addToast({ variant: 'error', title: 'Could not copy', messages: ['Copy the booking link manually instead.'] }))
    }
  }

  const handleRemoveFromPage = async (eventType: SchedulingEventType) => {
    const eventTypeId = eventType.id
    const result = await removeEventTypeFromUserPage({ accountId, userId: authenticatedUser.id, eventTypeId })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not remove the event type', messages: ['Please try again.'] }))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_EVENT_TYPE_REMOVED_FROM_BOOKING_PAGE, eventDetails: { eventTypeId } })
    dispatch(addToast({ variant: 'success', title: 'Removed from your page', messages: [`${eventType.name} no longer shows on your booking page.`] }))
  }

  const handlePreview = (eventType: SchedulingEventType) => {
    const url = eventTypeUrl(eventType)
    if (!url) {
      Sentry.captureException(new Error('Scheduling: missing account or user slug; cannot build an event type booking link'))
      dispatch(addToast({ variant: 'error', title: 'Could not open', messages: ['The booking page is unavailable.'] }))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_BOOKING_PAGE_VIEWED, eventDetails: { source: 'profile', eventTypeId: eventType.id } })
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <SettingsSection
      title='Your event types'
      subtitle='Set up the event types your clients can book.'
      titleAdornment={<EventTypesInfoTooltip />}
      isSaving={isSaving}
      action={
        addableEventTypes.length > 0 ? (
          <Button variant='primary' className='text-nowrap' onClick={() => setIsAddModalOpen(true)}>
            <i className='bi bi-plus-circle me-1' />
            Add shared event type
          </Button>
        ) : undefined
      }
    >
      {isLoading || isLoadingUserIds ? (
        <div className='d-flex justify-content-center py-4'>
          <Spinner />
        </div>
      ) : myEventTypes.length === 0 ? (
        <div className='d-flex flex-column align-items-center text-center gap-1 py-5 rounded-3 zocks-bg-color-gray-100'>
          <span className='bold'>Add your first event type to your booking page</span>
          <span className='paragraph-small zocks-text-gray-600'>Use shared event types in your account.</span>
        </div>
      ) : (
        <SettingsCard>
          <EventTypeList
            eventTypes={myEventTypes}
            editLabel='View'
            rowStatus={(eventType) => (isCoHostOf(eventType) ? <CoHostTag label='You’re also a co-host' /> : null)}
            onEdit={(eventType) =>
              navigate(`/workspace/setup/scheduling/scheduling-event-type/edit/${eventType.id}/details`, {
                state: { origin: '/account/profile-settings/scheduling', mode: 'view' },
              })
            }
            onPreview={handlePreview}
            onCopyLink={(eventType) => void handleCopyLink(eventType)}
            onRemoveFromPage={(eventType) => void handleRemoveFromPage(eventType)}
          />
        </SettingsCard>
      )}
      {isAddModalOpen ? (
        <AddSharedEventTypesModal eventTypes={addableEventTypes} isLoading={isAdding} onCancel={() => setIsAddModalOpen(false)} onAdd={handleAddShared} />
      ) : null}
    </SettingsSection>
  )
}
